import { z } from "zod";
import { IsoTimestamp, MatchId, NightId, PlayerId } from "./ids.js";
import { LinkIngestMethod, Match, MatchScore, VarVerdict } from "./match.js";

// ─────────────────────────────────────────────────────────────────────────────
// LOOP 1 API CONTRACTS v0.3 — the match-engine service surface.
// AUTH (D-010): every player endpoint takes `Authorization: Bearer <supabase_jwt>`
// (the D-009 token) — stateless verification with SUPABASE_JWT_SECRET, sub =
// player_id. DUGOUT endpoints use the service role + operator identity header.
// Errors are ApiError with documented codes per endpoint.
// ─────────────────────────────────────────────────────────────────────────────

/** Public identity — SAFE columns only, backed by the player_identity view
 *  (migration 0002). Never carries msisdn_hash/coins/confidence.
 *  v0.4 (centre CCR): GET /me → PlayerIdentity — the caller's own identity for
 *  the takeover/pinned "YOU" surfaces. Same shape, no envelope. */
export const PlayerIdentity = z.object({
  id: PlayerId,
  gamertag: z.string(),
  badge_id: z.string(),
  avatar_id: z.string(),
  founder: z.boolean(),
  division: z.number().int().min(1),
});
export type PlayerIdentity = z.infer<typeof PlayerIdentity>;

// ── Feed (MATCH POINT §5.1 — the card stack) ─────────────────────────────────
export const FeedCard = z.object({
  match_id: MatchId,
  night_id: NightId,
  host: PlayerIdentity,
  card_live_at: IsoTimestamp,
  expires_at: IsoTimestamp, // card_live_at + 5:00 (the ring; EA window buffer is the design)
  // v0.4 (feed CCR): the §5.9 host-economy values the card's WIN chip renders —
  // server-supplied (engine config), never hardcoded client-side (Rule 1).
  // nullish = additive: engine returns it as of the Loop 1 close.
  reward: z
    .object({
      host_coins_create: z.number().int(), // create-grant (+5 default, unjoined-capped)
      host_coins_claimed: z.number().int(), // join-grant (+5 default, uncapped)
    })
    .nullish(),
});
export type FeedCard = z.infer<typeof FeedCard>;

export const FeedResponse = z.object({
  cards: z.array(FeedCard), // expiring-soonest first; viewer's recent opponent filtered (§5.4b R2)
  total_live: z.number().int().min(0), // excludes cards hidden for THIS viewer
  your_hosted: FeedCard.nullable(), // MAX_HOSTED=1 — the pinned own card
});
export type FeedResponse = z.infer<typeof FeedResponse>;

// ── Create / arm (OPEN PLAY wizard W3, share relay §5.5) ─────────────────────
// Server parses the EA share payload — ONE parser, server-side truth. Errors:
// max_hosted · night_closed · night_unknown · bad_link · not_ready.
export const CreateMatchRequest = z.object({
  night_code: z.string().min(1),
  shared_text: z.string().min(1), // raw share-sheet/paste/typed payload
  method: LinkIngestMethod,
});
export type CreateMatchRequest = z.infer<typeof CreateMatchRequest>;

/** v0.4 ratification (contract-change request #3): THE `{match: Match}`
 *  envelope the engine answers state-moving writes with — create, claim (plus
 *  ea_join_url, see ClaimResponse), withdraw, link-opened, result, verify and
 *  release all return this shape. Clients re-fetch GET /match/{id} when they
 *  need the full MatchStateResponse truth. */
export const MatchEnvelope = z.object({ match: Match });
export type MatchEnvelope = z.infer<typeof MatchEnvelope>;

export const CreateMatchResponse = z.object({ match: Match });
export type CreateMatchResponse = z.infer<typeof CreateMatchResponse>;

export const WithdrawRequest = z.object({ match_id: MatchId });
export type WithdrawRequest = z.infer<typeof WithdrawRequest>;

/** v0.4 (feed CCR — PULLED UP escape hatch): POST /match/release — the HOST
 *  releases a CLAIMED match pre-kickoff. Engine rules (ARCHITECT ruling):
 *  CLAIMED → VOIDED via match_voided, reason_code 'host_released'; the joiner
 *  is freed with the §PULLED-UP consolation coin grant; the host takes a
 *  confidence_changed hit (config 'confidence.release_delta', default −2 —
 *  "dents your Confidence"). Returns MatchEnvelope.
 *  Errors: match_unknown · not_host · not_releasable (state ≠ CLAIMED). */
export const ReleaseRequest = z.object({ match_id: MatchId });
export type ReleaseRequest = z.infer<typeof ReleaseRequest>;

// ── Claim (atomic, first tap wins — §5.1) ────────────────────────────────────
// Errors: snapped (already claimed) · own_card · recent_opponent · expired.
export const ClaimRequest = z.object({ match_id: MatchId });
export type ClaimRequest = z.infer<typeof ClaimRequest>;

export const ClaimResponse = z.object({
  match: Match,
  ea_join_url: z.string().url(), // revealed ONLY to the claimer, at claim time
});
export type ClaimResponse = z.infer<typeof ClaimResponse>;

export const LinkOpenedRequest = z.object({ match_id: MatchId });
export type LinkOpenedRequest = z.infer<typeof LinkOpenedRequest>;

// ── Result / verify / dispute (§5.6, FAIR RESULT tier 1) ─────────────────────
export const SubmitResultRequest = z.object({
  match_id: MatchId,
  score: MatchScore,
  screenshot_sha256: z.string().length(64).nullable(), // evidence attaches async
});
export type SubmitResultRequest = z.infer<typeof SubmitResultRequest>;

export const VerifyResultRequest = z.object({
  match_id: MatchId,
  action: z.enum(["verify", "accept_theirs", "correct_mine", "dispute"]),
  corrected_score: MatchScore.optional(), // correct_mine only
  note: z.string().max(140).optional(), // dispute only ("Evidence talks.")
});
export type VerifyResultRequest = z.infer<typeof VerifyResultRequest>;

/** GET /match/{id} — the Match Centre's single truth. */
export const MatchStateResponse = z.object({
  match: Match,
  you_are: z.enum(["host", "joiner"]),
  opponent: PlayerIdentity.nullable(),
  your_submission: MatchScore.nullable(),
  their_submission_present: z.boolean(), // never their score before you submit? — no: verify screen shows it
  their_submission: MatchScore.nullable(),
  ea_join_url: z.string().url().nullable(), // participants only, live states only
  // v0.4 (centre CCR #1/#2 — UPSTAIRS: the clock is a promise, server-anchored).
  // nullish = additive: engine returns them as it wires the anchors (Loop 1 close).
  me: PlayerIdentity.nullish(), // your own identity for the VS home side
  lobby_expires_at: IsoTimestamp.nullish(),
  auto_confirm_at: IsoTimestamp.nullish(),
  disputed_at: IsoTimestamp.nullish(),
});
export type MatchStateResponse = z.infer<typeof MatchStateResponse>;

// ── Match chat (TOUCHLINE A2 — presets first, on the record) ─────────────────
export const ChatPostRequest = z.object({
  match_id: MatchId,
  preset: z.string().nullable(),
  body: z.string().min(1).max(280).nullable(),
});
export type ChatPostRequest = z.infer<typeof ChatPostRequest>;

export const ChatMessageOut = z.object({
  id: z.string().uuid(),
  match_id: MatchId,
  player: PlayerIdentity,
  preset: z.string().nullable(),
  body: z.string().nullable(),
  created_at: IsoTimestamp,
});
export type ChatMessageOut = z.infer<typeof ChatMessageOut>;

/** v0.4 ratification (PROOFER BREAK-1): GET /match/{id}/chat answers THIS
 *  envelope — the engine's shape wins; the client unwraps. */
export const ChatListResponse = z.object({ messages: z.array(ChatMessageOut) });
export type ChatListResponse = z.infer<typeof ChatListResponse>;

// ── Evidence (FAIR RESULT §3 — compressed client-side, async upload) ─────────
export const EvidenceUploadRequest = z.object({
  match_id: MatchId,
  sha256: z.string().length(64),
  phash: z.string().min(8),
  width: z.number().int().min(1),
  height: z.number().int().min(1),
  content_base64: z.string().min(1), // ≤600KB post-compression contract (client enforces)
});
export type EvidenceUploadRequest = z.infer<typeof EvidenceUploadRequest>;

// ── Leaderboard v1 (THE TABLE items 1–3) ─────────────────────────────────────
export const LeaderboardRow = z.object({
  rank: z.number().int().min(1),
  player: PlayerIdentity,
  points: z.number().int(),
  delta: z.number().int(), // vs last standings tick — ▲2 / ▼1 / 0
  in_money: z.boolean(),
});
export type LeaderboardRow = z.infer<typeof LeaderboardRow>;

export const LeaderboardResponse = z.object({
  night_id: NightId,
  rows: z.array(LeaderboardRow),
  you: LeaderboardRow.nullable(),
  money_line_rank: z.number().int().nullable(), // the gold rule's cutoff
});
export type LeaderboardResponse = z.infer<typeof LeaderboardResponse>;

// ── DUGOUT VAR console v1 (assist-mode; keys 1–5, reason-coded) ──────────────
export const VarCaseSummary = z.object({
  match_id: MatchId,
  night_id: NightId,
  raised_at: IsoTimestamp,
  host: PlayerIdentity,
  joiner: PlayerIdentity,
  host_score: MatchScore.nullable(),
  joiner_score: MatchScore.nullable(),
  sla_ms: z.number().int().min(0),
});
export type VarCaseSummary = z.infer<typeof VarCaseSummary>;

// v0.4: shared reason vocabulary — every verdict is a training label (FAIR RESULT §5.3).
export const VarReasonCode = z.enum([
  "score_evidence_clear",
  "no_show_ledger",
  "presence_ambiguous",
  "fabrication_finding",
  "procedural",
]);
export type VarReasonCode = z.infer<typeof VarReasonCode>;

export const VarVerdictRequest = z.object({
  match_id: MatchId,
  verdict: VarVerdict,
  reason_code: VarReasonCode, // structured picklist — never free-prose-only
  note: z.string().max(500).optional(),
  // WALKOVER: DUGOUT names the winner explicitly; omitted ⇒ engine default (the disputer).
  awarded_to: PlayerId.optional(),
});
export type VarVerdictRequest = z.infer<typeof VarVerdictRequest>;

export const VarVerdictResponse = z.object({
  ok: z.literal(true),
  match_state: z.string(), // the resulting MatchState — the ack names the outcome
});
export type VarVerdictResponse = z.infer<typeof VarVerdictResponse>;

export const VarQueueResponse = z.object({
  cases: z.array(VarCaseSummary),
  agent_agreement_pct: z.number().min(0).max(100).nullable(), // null until Loop 4's agent runs
});
export type VarQueueResponse = z.infer<typeof VarQueueResponse>;

// GET /dugout/var/case/{match_id} — the case room's full record (VAR CCR #1).
export const VarLedgerLine = z.object({
  at: IsoTimestamp,
  event_type: z.string(),
  actor: z.string().nullable(), // gamertag or 'system'/'dugout:{operator}'
  detail: z.string().nullable(),
});
export type VarLedgerLine = z.infer<typeof VarLedgerLine>;

export const VarCaseDetail = z.object({
  summary: VarCaseSummary,
  ledger: z.array(VarLedgerLine),
  dispute_note: z.string().nullable(),
});
export type VarCaseDetail = z.infer<typeof VarCaseDetail>;

export const NightOpenRequest = z.object({ night_code: z.string().min(1) });
export type NightOpenRequest = z.infer<typeof NightOpenRequest>;
